import React from "react";
import { Printer, ChevronUp, ChevronDown } from "lucide-react";
import {
  PAPER_SIZE_OPTIONS,
  columnsForPaperSize,
  defaultPrinterPrintConfig,
} from "../utils/thermalPrinterConfig.js";

const PrinterCard = ({
  printer,
  expandedPrinterId,
  connectingPrinter,
  printerDetails,
  LOCATIONS,
  onPrinterClick,
  onPrinterDetailChange,
  onTestPrint,
  onSavePrinter,
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSavePrinter(printer);
  };

  const details =
    printerDetails[printer.innerMacAddress] ||
    defaultPrinterPrintConfig({ ...printer });

  const handlePaperSizeChange = (paperSize) => {
    onPrinterDetailChange(printer, "paperSize", paperSize);
    onPrinterDetailChange(printer, "width", columnsForPaperSize(paperSize));
  };

  return (
    <div className="bg-white rounded-xl border hover:border-blue-500 transition-all h-full">
      {/* Printer Header */}
      <div
        onClick={() => onPrinterClick(printer)}
        className="p-4 flex items-center justify-between cursor-pointer"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Printer className="w-5 h-5 text-blue-500" />
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-gray-900 truncate">
              {printer.deviceName}
            </h3>
            <p className="text-sm text-gray-500 truncate">
              {printer.innerMacAddress}
            </p>
            {connectingPrinter === printer.innerMacAddress &&
              !printer.isConnected && (
                <p className="text-sm text-blue-500">Connecting...</p>
              )}
            {printer.isConnected && (
              <p className="text-sm text-green-500">Connected</p>
            )}
            {printer.connectionError && (
              <p className="text-sm text-red-500">{printer.connectionError}</p>
            )}
          </div>
        </div>
        {expandedPrinterId === printer.innerMacAddress &&
        !printer.isConnected ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </div>

      {/* Expanded Configuration */}
      {printer.isConnected && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-4 mt-4">
            <form onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Printer Name
                </label>
                <input
                  type="text"
                  required
                  value={details?.name || ""}
                  onChange={(e) =>
                    onPrinterDetailChange(printer, "name", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Enter printer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={details?.location || ""}
                  required
                  onChange={(e) =>
                    onPrinterDetailChange(printer, "location", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">Select printer type</option>
                  {LOCATIONS.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paper size
                </label>
                <select
                  value={details?.paperSize || "58mm"}
                  required
                  onChange={(e) => handlePaperSizeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  {PAPER_SIZE_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Width (characters)
                </label>
                <input
                  type="number"
                  value={details?.width ?? columnsForPaperSize(details?.paperSize)}
                  onChange={(e) =>
                    onPrinterDetailChange(printer, "width", e.target.value)
                  }
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <p className="text-xs text-gray-500">
                  Auto-set from paper size. Override only if test print wraps
                  (common: 58mm → 32, 80mm → 48).
                </p>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => onTestPrint(printer)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm"
                >
                  Test Print
                </button>
                <button
                  type="submit"
                  disabled={
                    !details?.name ||
                    !details?.location
                  }
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  Save Configuration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrinterCard;
